/**
 * FeeModalManager.tsx
 * Centralized modal state management for fee configuration
 */

import React from 'react';
import { useDisclosure } from '@enfomentis/ui';
import { FeeAssignmentModal, AssetClassSelectorModal, InstrumentSelectionModal, type InstrumentSelection } from '../modals';
import type { AssetClassType } from '@/types/shared/AssetClass';
import type { FeeFormData } from '../utils/feeFormTransforms';
import { ModalType, type ModalContext } from '../utils/modalUtils';


// Existing hierarchy levels structure
interface ExistingHierarchyLevels {
  assetClasses: string[];
  marketSegments: Record<string, string[]>; // assetClass -> marketSegments[]
  subAssetClasses: Record<string, string[]>; // "assetClass-marketSegment" -> subAssetClasses[]
}

// Props for the modal manager component
interface FeeModalManagerProps {
  context: ModalContext | null;
  activeModal: ModalType;
  onClose: () => void;
  onFeeAssignmentSave: (feeData: FeeFormData) => void;
  onAssetClassAdd: (selection: { level: string; value: string; customValue: string; label: string; }) => void;
  onInstrumentImport: (instruments: InstrumentSelection[]) => void;
  existingInstruments: string[];
  existingHierarchyLevels: ExistingHierarchyLevels;
}

export const FeeModalManager: React.FC<FeeModalManagerProps> = ({
  context,
  activeModal,
  onClose,
  onFeeAssignmentSave,
  onAssetClassAdd,
  onInstrumentImport,
  existingInstruments,
  existingHierarchyLevels
}) => {
  const { isOpen: isFeeAssignmentOpen, onOpen: onFeeAssignmentOpen, onClose: onFeeAssignmentClose } = useDisclosure();
  const { isOpen: isAssetClassSelectorOpen, onOpen: onAssetClassSelectorOpen, onClose: onAssetClassSelectorClose } = useDisclosure();
  const { isOpen: isInstrumentSelectionOpen, onOpen: onInstrumentSelectionOpen, onClose: onInstrumentSelectionClose } = useDisclosure();

  // Handle modal opening based on activeModal state
  React.useEffect(() => {
    if (activeModal === ModalType.FEE_ASSIGNMENT) {
      onFeeAssignmentOpen();
    } else if (activeModal === ModalType.ASSET_CLASS_SELECTOR) {
      onAssetClassSelectorOpen();
    } else if (activeModal === ModalType.INSTRUMENT_SELECTION) {
      onInstrumentSelectionOpen();
    }
  }, [activeModal, onFeeAssignmentOpen, onAssetClassSelectorOpen, onInstrumentSelectionOpen]);

  // Wrapper handlers to ensure modal state is properly cleared
  const handleFeeAssignmentClose = () => {
    onFeeAssignmentClose();
    onClose();
  };

  const handleAssetClassSelectorClose = () => {
    onAssetClassSelectorClose();
    onClose();
  };

  const handleInstrumentSelectionClose = () => {
    onInstrumentSelectionClose();
    onClose();
  };

  const handleFeeAssignmentSave = (feeData: FeeFormData) => {
    onFeeAssignmentSave(feeData);
    handleFeeAssignmentClose();
  };

  const handleAssetClassAdd = (selection: { level: string; value: string; customValue: string; label: string; }) => {
    onAssetClassAdd(selection);
    handleAssetClassSelectorClose();
  };

  const handleInstrumentImport = (instruments: InstrumentSelection[]) => {
    onInstrumentImport(instruments);
    handleInstrumentSelectionClose();
  };

  if (!context) {
    return null;
  }

  return (
    <>
      <FeeAssignmentModal
        isOpen={isFeeAssignmentOpen}
        onClose={handleFeeAssignmentClose}
        title={context.title}
        subtitle={context.subtitle || ''}
        {...(context.existingFees && { existingFees: context.existingFees })}
        onSave={handleFeeAssignmentSave}
      />

      <AssetClassSelectorModal
        isOpen={isAssetClassSelectorOpen}
        onClose={handleAssetClassSelectorClose}
        level={context.hierarchyLevel || 'asset-class'}
        {...(context.parentAssetClass ? { parentAssetClass: context.parentAssetClass as AssetClassType } : {})}
        existingHierarchyLevels={existingHierarchyLevels}
        onAdd={handleAssetClassAdd}
      />

      <InstrumentSelectionModal
        isOpen={isInstrumentSelectionOpen}
        onClose={handleInstrumentSelectionClose}
        onImport={handleInstrumentImport}
        existingInstruments={existingInstruments}
        filterContext={context.parentAssetClass ? (() => {
          const filterContext: { assetClass?: string; marketSegment?: string; subAssetClass?: string } = {
            assetClass: context.parentAssetClass
          };
          if (context.parentMarketSegment) {
            filterContext.marketSegment = context.parentMarketSegment;
          }
          if (context.parentSubAssetClass) {
            filterContext.subAssetClass = context.parentSubAssetClass;
          }
          return filterContext;
        })() : undefined}
      />
    </>
  );
};